import LayoutShape, {
    LayoutComponentShape,
    LayoutContainerShape,
} from 'layout/LayoutShape';
import {
    initialLayoutState,
    Layout,
    LayoutState,
    LayoutStatus,
} from 'layout/LayoutState';
import {
    LayoutAction,
    LayoutActionType,
    LayoutStateChangeSubtype,
} from './actions';
import { Reducer } from 'redux';

const layoutReducer: Reducer<LayoutState, LayoutAction> = (
    state = initialLayoutState,
    action
) => {
    switch (action.type) {
        case LayoutActionType.LAYOUT_RESET: {
            return { ...initialLayoutState, manualSave: state.manualSave };
        }
        case LayoutActionType.LAYOUT_SET_PANEL_ID: {
            return { ...state, panelId: action.payload! };
        }
        case LayoutActionType.LAYOUT_SET_MANUAL_SAVE: {
            return { ...state, manualSave: action.payload };
        }
        case LayoutActionType.LAYOUT_SET_DIRTY: {
            return { ...state, isDirty: action.payload };
        }
        case LayoutActionType.LAYOUT_SET_ACTIVE_INPUT: {
            const nextState = { ...state };
            nextState.result!.activeInputId = action.payload!.activeInputId;
            return nextState;
        }
        case LayoutActionType.LAYOUT_SAVED: {
            const layout = state.result;
            let nextLayout = layout;
            if (
                layout &&
                layout.name === action.payload!.layoutName &&
                layout.origin === action.payload!.origin &&
                layout.id === null
            ) {
                nextLayout = { ...layout, id: action.payload!.id };
            }
            return { ...state, result: nextLayout, isDirty: false };
        }
        case LayoutActionType.LAYOUT_CLEAR_PENDING_LAYOUT: {
            const nextState = { ...state };
            delete nextState.pendingLayout;
            return nextState;
        }
        case LayoutActionType.LAYOUT_STATE_CHANGE: {
            const layoutState = action.payload!.layoutState;
            switch (layoutState.status) {
                case LayoutStatus.NONE:
                case LayoutStatus.PENDING:
                    return {
                        ...state,
                        status: layoutState.status,
                        error: undefined,
                        result: state.result!,
                        pendingLayout:
                            action.payload!.layoutState.pendingLayout,
                    };
                case LayoutStatus.SUCCESS:
                    return {
                        ...state,
                        status: LayoutStatus.SUCCESS,
                        error: undefined,
                        result: layoutState.result!
                            ? computeNextLayoutSuccess(layoutState.result!)
                            : state.result!,
                        isDirty:
                            action.payload?.subType ===
                            LayoutStateChangeSubtype.LOAD
                                ? false
                                : state.isDirty,
                    };
                case LayoutStatus.ERROR: {
                    const nextState: LayoutState = {
                        ...state,
                        status: LayoutStatus.ERROR,
                        error: layoutState.error,
                        result: {
                            id: null,
                            name: action.payload!.layoutState.error!.name,
                            origin: action.payload!.layoutState.error!.origin,
                            shape: { type: 'row', content: [] },
                            components: [],
                            activeInputId: undefined,
                        },
                    };
                    delete nextState.pendingLayout;
                    return nextState;
                }
            }
        }
    }
    return state;
};

function computeNextLayoutSuccess(layout: Layout) {
    const nextLayout = { ...layout };
    nextLayout.components = [];

    if (nextLayout.shape) {
        // Standalone mode, components are driven by the shape...
        const nextComponents =
            extractComponentsAndFirstActiveInputFromLayout(layout);

        nextLayout.components = nextComponents.map((c) => ({
            id: c.componentId,
            type: c.componentType,
        }));
    } else {
        // Widgets mode...
        nextLayout.components = [...layout.components];
    }

    return nextLayout;
}

function extractComponentsAndFirstActiveInputFromLayout(nextLayout: Layout) {
    const nextComponents: LayoutComponentShape[] = [];
    visit(nextLayout && nextLayout.shape, (c) => {
        if (c.type === 'component') {
            nextComponents.push(c);
        }
    });
    return nextComponents;
}

export function visit(
    shape: LayoutShape | undefined,
    visitor: (shape: LayoutShape) => void
) {
    if (!shape) {
        return;
    }
    visitor(shape);
    if (shape.type !== 'component') {
        ((shape as LayoutContainerShape).content || []).forEach((s) =>
            visit(s, visitor)
        );
    }
}

export default layoutReducer;
